import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ImageContainer, ImagePreview, ImagesContainer, Wrapper } from './styled';
import { imageUrls } from '../const/images-urls';
import { userMediaStore } from '@/shared/model/user-media';
import { FileInputUploader } from '@/widgets/Profile/model/ProfileForm/styled';
import { ResetPictureButton, UploadButton } from '@/pages/SignUp/model/SignUpForm/styled';
import { usePictureUploader } from '@/shared/lib/hooks';
import ImageSizeAlert from '@/entities/notifications/ImageSizeAlert';
import { ExplicitContentAlert } from '@/entities/notifications/ExplicitContentAlert';
import { modalStore } from '@/shared/model/modal';
import { TrashCanIcon } from '@/shared/assets/icons';
import { STORAGE_KEYS } from '@/shared/constants/common';
import StorageService from '@/shared/lib/utils/storage';
export const BackgroundImagePicker = observer(() => {
    const { userVideoBackgroundImg } = userMediaStore;
    const { enqueueSnackbar } = useSnackbar();
    const [customBackgrounds, setCustomBackgrounds] = useState([
        ...imageUrls,
        ...(StorageService.get(STORAGE_KEYS.backgrounds) ?? []),
    ]);
    const handleSelect = useCallback((image) => () => {
        userMediaStore.userVideoBackgroundImg = image;
    }, []);
    const inputPictureRef = useRef(null);
    const { key, fileData, loading: pictureLoading, handleFileChange, handleFileReset, } = usePictureUploader((maxSizeMB) => {
        modalStore.open({
            content: _jsx(ImageSizeAlert, { maxSizeMB: maxSizeMB }),
        });
    }, () => {
        modalStore.open({
            content: _jsx(ExplicitContentAlert, {}),
        });
    });
    const handleUploadClick = () => {
        inputPictureRef.current?.click();
    };
    const handleRemove = (file) => () => {
        handleFileReset();
        setCustomBackgrounds((prev) => {
            const backgrounds = prev.filter((f) => f !== file);
            StorageService.set(STORAGE_KEYS.backgrounds, backgrounds.filter((b) => imageUrls.every((i) => i !== b)));
            return backgrounds;
        });
        if (file === userVideoBackgroundImg) {
            handleSelect('')();
        }
    };
    useEffect(() => {
        if (!fileData?.data) {
            return;
        }
        if (customBackgrounds.includes(fileData.data)) {
            enqueueSnackbar('Image already exists!', {
                autoHideDuration: 2000,
                variant: 'notification',
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom',
                },
            });
            return;
        }
        setCustomBackgrounds((prev) => {
            if (!fileData?.data) {
                return prev;
            }
            return [...prev, fileData.data];
        });
        handleFileReset();
    }, [customBackgrounds, enqueueSnackbar, fileData, handleFileReset]);
    useEffect(() => {
        StorageService.set(STORAGE_KEYS.backgrounds, customBackgrounds.filter((b) => imageUrls.every((i) => i !== b)));
    }, [customBackgrounds]);
    return (_jsxs(ImagesContainer, { children: [_jsx(ImageContainer, { onClick: handleSelect(''), selected: !userVideoBackgroundImg, children: "Without" }), customBackgrounds.map((image) => (_jsxs(Wrapper, { children: [_jsx(ImageContainer, { selected: userVideoBackgroundImg === image, children: _jsx(ImagePreview, { onClick: handleSelect(image), src: image, alt: '' }) }), imageUrls.every((i) => i !== image) && (_jsx(ResetPictureButton, { onClick: handleRemove(image), children: _jsx(TrashCanIcon, {}) }))] }, image))), pictureLoading && (_jsx(ImageContainer, { selected: false, children: _jsx(CircularProgress, {}) })), customBackgrounds.length - imageUrls.length + +pictureLoading < 3 && (_jsxs(ImageContainer, { selected: false, children: [_jsx(UploadButton, { onClick: handleUploadClick, type: 'button', children: "+" }), _jsx(FileInputUploader, { type: 'file', ref: inputPictureRef, onChange: handleFileChange, accept: '.png,.jpg,.jpeg', "aria-hidden": true, "aria-busy": pictureLoading }, key)] }))] }));
});
